"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/libs/utils";
import { cva, VariantProps } from "class-variance-authority";
import TooltipArrowIcon from "@/v2-components/ui/_icons/tooltip-arrow-icon";

const TooltipProvider = TooltipPrimitive.Provider;

const TooltipRoot = TooltipPrimitive.Root;

const TooltipPortal = TooltipPrimitive.Portal;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipArrow = TooltipPrimitive.Arrow;

const tooltipContentVariants = cva(
  ["z-50 rounded-lg px-4 py-3 text-sm break-all"],
  {
    variants: {
      variant: {
        dark: "bg-black/80 text-coolgray-10",
        light: "bg-white text-coolgray-90 drop-shadow-md",
      },
    },
    defaultVariants: {
      variant: "dark",
    },
  },
);

const tooltipArrowVariants = cva("", {
  variants: {
    variant: {
      dark: "text-black/80",
      light: "text-white",
    },
  },
  defaultVariants: {
    variant: "dark",
  },
});

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> &
    VariantProps<typeof tooltipContentVariants>
>(({ className, children, side, variant, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    alignOffset={side === "left" || side === "right" ? -8 : 0}
    sideOffset={sideOffset}
    side={side}
    className={cn(tooltipContentVariants({ variant }), className)}
    {...props}
  >
    {children}
    <TooltipArrow asChild>
      <TooltipArrowIcon className={cn(tooltipArrowVariants({ variant }))} />
    </TooltipArrow>
  </TooltipPrimitive.Content>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

const Tooltip = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> &
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root> &
    VariantProps<typeof tooltipContentVariants> & {
      tooltipContent?: React.ReactNode;
      portal?: boolean;
    }
>((props, ref) => {
  const {
    open,
    onOpenChange,
    children,
    tooltipContent,
    sideOffset = 4,
    delayDuration = 0,
    portal = false,
    ...rest
  } = props;

  const [_open, setOpen] = React.useState(false);

  const Wrapper = portal ? TooltipPortal : React.Fragment;

  return (
    <TooltipProvider>
      <TooltipRoot
        open={open || _open}
        onOpenChange={setOpen}
        delayDuration={delayDuration}
      >
        <TooltipTrigger
          onClick={(e) => {
            e.stopPropagation();
            setOpen((prev) => !prev);
          }}
          asChild
        >
          {children}
        </TooltipTrigger>
        <Wrapper>
          <TooltipContent sideOffset={sideOffset} {...rest} ref={ref}>
            {tooltipContent}
          </TooltipContent>
        </Wrapper>
      </TooltipRoot>
    </TooltipProvider>
  );
});
Tooltip.displayName = "Tooltip";

export {
  Tooltip,
  TooltipTrigger,
  TooltipArrow,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
};
